import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import moment from 'moment';
import Api from '../../../../Api';
import MyLoading from '../../../../components/MyLoading';
import TablePagination from '../../../../components/TablePagination';
import './css/index.scss';

const PER_PAGE = 50;

const DATE_PRESET = {
  month: 'month',
  week: 'week',
  yesterday: 'yesterday',
  today: 'today',
  all: 'all',
  custom: 'custom',
};

const SOURCES = [
  { value: 'ticketmaster', label: 'Ticketmaster' },
  { value: 'eventim', label: 'Eventim' },
  { value: 'fienta', label: 'Fienta' },
  { value: 'kontramarka', label: 'Контрамарка' },
  { value: 'israelinfo', label: 'Israelinfo' },
  { value: 'showman', label: 'Showman' },
];

/** Подписи групп. reason_text с сервера — пример последнего запроса, не название группы. */
const REASON_LABELS = {
  description_web_search: 'Веб-поиск для описания',
  description_rewrite_grounded: 'Описание по найденным фактам',
  description_suspicion: 'Проверка описания',
  category_web_search: 'Веб-поиск для категории',
  category_web: 'Категория по странице мероприятия',
  category_ai: 'Категория без веб-поиска',
};

const REASON_OPTIONS = Object.entries(REASON_LABELS).map(([value, label]) => ({ value, label }));

function computeRangeForPreset(preset) {
  const now = moment();
  switch (preset) {
    case DATE_PRESET.month:
      return { from: now.clone().subtract(1, 'month'), to: now.clone() };
    case DATE_PRESET.week:
      return { from: now.clone().subtract(7, 'days').startOf('day'), to: now.clone() };
    case DATE_PRESET.yesterday: {
      const start = now.clone().subtract(1, 'day').startOf('day');
      return { from: start, to: start.clone().add(1, 'day') };
    }
    case DATE_PRESET.today:
      return { from: now.clone().startOf('day'), to: now.clone() };
    default:
      return { from: now.clone().subtract(1, 'month'), to: now.clone() };
  }
}

function presetToLocalStrings(preset) {
  const { from, to } = computeRangeForPreset(preset);
  return {
    fromLocal: from.format('YYYY-MM-DDTHH:mm'),
    toLocal: to.format('YYYY-MM-DDTHH:mm'),
  };
}

function reasonLabel(code) {
  if (!code) return '—';
  return REASON_LABELS[code] || code;
}

function sourceLabel(code) {
  if (!code) return '—';
  return SOURCES.find((item) => item.value === code)?.label || code;
}

function formatInt(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return new Intl.NumberFormat('ru-RU').format(Math.round(number));
}

function formatUsd(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  const abs = Math.abs(number);
  const digits = abs === 0 || abs >= 0.01 ? 4 : 6;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: abs === 0 ? 2 : digits,
  }).format(number);
}

function formatWhen(iso) {
  if (!iso) return '—';
  const date = moment(iso);
  if (!date.isValid()) return '—';
  return date.format('DD.MM.YYYY HH:mm');
}

function rangeError(datePreset, fromLocal, toLocal) {
  if (datePreset === DATE_PRESET.all) return '';
  const from = moment(fromLocal);
  const to = moment(toLocal);
  if (!from.isValid() || !to.isValid()) return 'Укажите корректные даты';
  if (!to.isAfter(from)) return 'Конец периода должен быть позже начала';
  return '';
}

function sharePercent(row, totals) {
  const cost = Number(totals?.cost_usd) || 0;
  if (cost > 0) return ((Number(row.cost_usd) || 0) / cost) * 100;
  const requests = Number(totals?.requests) || 0;
  if (requests > 0) return ((Number(row.requests) || 0) / requests) * 100;
  return 0;
}

function collectUnknownReasons(body) {
  const found = [];
  const seen = new Set(Object.keys(REASON_LABELS));
  const consider = (code) => {
    if (!code || seen.has(code)) return;
    seen.add(code);
    found.push(code);
  };
  (body?.by_reason || []).forEach((row) => consider(row.reason));
  (body?.items || []).forEach((item) => consider(item.reason));
  return found;
}

function OpenAiUsage() {
  const initialRange = presetToLocalStrings(DATE_PRESET.month);
  const [datePreset, setDatePreset] = useState(DATE_PRESET.month);
  const [fromLocal, setFromLocal] = useState(initialRange.fromLocal);
  const [toLocal, setToLocal] = useState(initialRange.toLocal);
  const [source, setSource] = useState('');
  const [reason, setReason] = useState('');
  const [parseRunInput, setParseRunInput] = useState('');
  const [parseRun, setParseRun] = useState('');
  const [page, setPage] = useState(1);
  const [discoveredReasons, setDiscoveredReasons] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const queryKeyRef = useRef(null);

  const applyParseRun = (value) => {
    const next = String(value || '').trim();
    setParseRunInput(next);
    if (next === parseRun) return;
    setParseRun(next);
    setPage(1);
  };

  const onDatePresetChange = (event) => {
    const next = event.target.value;
    setDatePreset(next);
    setPage(1);
    if (next !== DATE_PRESET.custom && next !== DATE_PRESET.all) {
      const range = presetToLocalStrings(next);
      setFromLocal(range.fromLocal);
      setToLocal(range.toLocal);
    }
  };

  const toggleReason = (value) => {
    setReason((current) => (current === value ? '' : value));
    setPage(1);
  };

  const resetFilters = () => {
    const range = presetToLocalStrings(DATE_PRESET.month);
    setDatePreset(DATE_PRESET.month);
    setFromLocal(range.fromLocal);
    setToLocal(range.toLocal);
    setSource('');
    setReason('');
    setParseRunInput('');
    setParseRun('');
    setPage(1);
  };

  const load = useCallback(async (cancelledRef) => {
    const validationError = rangeError(datePreset, fromLocal, toLocal);
    if (validationError) {
      setError(validationError);
      setData(null);
      setLoading(false);
      return;
    }

    const params = {
      page,
      per_page: PER_PAGE,
    };
    if (source) params.source = source;
    if (reason) params.reason = reason;
    if (parseRun) params.parse_run = parseRun;
    if (datePreset !== DATE_PRESET.all) {
      params.from = moment(fromLocal).toISOString();
      params.to = moment(toLocal).toISOString();
    }

    setLoading(true);
    setError('');
    try {
      const { data: body } = await Api.getOpenAiUsage(params);
      if (cancelledRef.current) return;
      if (!body || body.status !== 'ok') {
        setError(body?.message || 'Не удалось загрузить статистику');
        setData(null);
        return;
      }
      setData(body);
      const unknown = collectUnknownReasons(body);
      if (unknown.length) {
        setDiscoveredReasons((prev) => {
          const merged = [...prev];
          unknown.forEach((code) => {
            if (!merged.includes(code)) merged.push(code);
          });
          return merged.length === prev.length ? prev : merged;
        });
      }
    } catch (e) {
      if (cancelledRef.current) return;
      const message = e?.response?.data?.message || 'Не удалось загрузить статистику';
      if (page > 1 && /page/i.test(message)) {
        setPage(1);
        return;
      }
      setError(message);
      setData(null);
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [datePreset, fromLocal, toLocal, source, reason, parseRun, page]);

  useEffect(() => {
    const queryKey = `${source}|${reason}|${parseRun}|${datePreset}|${fromLocal}|${toLocal}`;
    if (queryKeyRef.current && queryKeyRef.current !== queryKey) {
      setData(null);
    }
    queryKeyRef.current = queryKey;

    const cancelledRef = { current: false };
    load(cancelledRef);
    return () => {
      cancelledRef.current = true;
    };
  }, [load]);

  const totals = data?.totals || null;
  const reasonRows = useMemo(() => {
    const rows = Array.isArray(data?.by_reason) ? [...data.by_reason] : [];
    rows.sort((a, b) => {
      const costDiff = (Number(b.cost_usd) || 0) - (Number(a.cost_usd) || 0);
      if (costDiff) return costDiff;
      return (Number(b.requests) || 0) - (Number(a.requests) || 0);
    });
    return rows;
  }, [data]);

  const items = Array.isArray(data?.items) ? data.items : [];
  const total = Number(data?.total) || 0;
  const pagesCount = Math.max(1, Math.ceil(total / (Number(data?.per_page) || PER_PAGE)));

  const reasonOptions = useMemo(() => {
    const options = [...REASON_OPTIONS];
    discoveredReasons.forEach((code) => {
      if (!options.some((item) => item.value === code)) {
        options.push({ value: code, label: code });
      }
    });
    if (reason && !options.some((item) => item.value === reason)) {
      options.push({ value: reason, label: reasonLabel(reason) });
    }
    return options;
  }, [discoveredReasons, reason]);

  const filtersAreDefault = datePreset === DATE_PRESET.month
    && !source
    && !reason
    && !parseRun
    && !parseRunInput;

  const cards = totals ? [
    { key: 'requests', label: 'Запросы', value: formatInt(totals.requests) },
    { key: 'prompt', label: 'Входные токены', value: formatInt(totals.prompt_tokens) },
    { key: 'completion', label: 'Выходные токены', value: formatInt(totals.completion_tokens) },
    { key: 'total', label: 'Всего токенов', value: formatInt(totals.total_tokens) },
    { key: 'search', label: 'Веб-поиск', value: formatInt(totals.web_search_calls) },
    {
      key: 'cost',
      label: 'Стоимость',
      value: formatUsd(totals.cost_usd),
      accent: true,
    },
  ] : [];

  return (
    <div className="ai_usage">
      <div className="ai_usage_filters">
        <div className="ai_usage_field">
          <label htmlFor="ai_period">Период</label>
          <select id="ai_period" className="ai_usage_select" value={datePreset} onChange={onDatePresetChange}>
            <option value={DATE_PRESET.month}>За месяц</option>
            <option value={DATE_PRESET.week}>За неделю</option>
            <option value={DATE_PRESET.yesterday}>За вчера</option>
            <option value={DATE_PRESET.today}>За сегодня</option>
            <option value={DATE_PRESET.all}>За всё время</option>
            <option value={DATE_PRESET.custom}>Другое</option>
          </select>
        </div>
        {datePreset === DATE_PRESET.custom ? (
          <>
            <div className="ai_usage_field">
              <label htmlFor="ai_from">С</label>
              <input
                id="ai_from"
                type="datetime-local"
                value={fromLocal}
                onChange={(event) => {
                  setFromLocal(event.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="ai_usage_field">
              <label htmlFor="ai_to">До</label>
              <input
                id="ai_to"
                type="datetime-local"
                value={toLocal}
                title="Эта граница не включается"
                onChange={(event) => {
                  setToLocal(event.target.value);
                  setPage(1);
                }}
              />
            </div>
          </>
        ) : null}
        <div className="ai_usage_field">
          <label htmlFor="ai_source">Источник</label>
          <select
            id="ai_source"
            className="ai_usage_select"
            value={source}
            onChange={(event) => {
              setSource(event.target.value);
              setPage(1);
            }}
          >
            <option value="">Все источники</option>
            {SOURCES.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </div>
        <div className="ai_usage_field">
          <label htmlFor="ai_reason">Тип запроса</label>
          <select
            id="ai_reason"
            className="ai_usage_select"
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              setPage(1);
            }}
          >
            <option value="">Все типы</option>
            {reasonOptions.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </div>
        <div className="ai_usage_field">
          <label htmlFor="ai_run">Прогон парсинга</label>
          <input
            id="ai_run"
            type="text"
            value={parseRunInput}
            placeholder="Id прогона"
            onChange={(event) => setParseRunInput(event.target.value)}
            onBlur={() => applyParseRun(parseRunInput)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                applyParseRun(parseRunInput);
              }
            }}
          />
        </div>
        {filtersAreDefault ? null : (
          <div className="ai_usage_field ai_usage_field_action">
            <button type="button" className="ai_usage_reset" onClick={resetFilters}>
              Сбросить
            </button>
          </div>
        )}
      </div>

      <div className="ai_usage_period">
        {datePreset === DATE_PRESET.all ? (
          <span>За всё время</span>
        ) : (
          <>
            <span>
              С
              {' '}
              {formatWhen(fromLocal)}
            </span>
            <span className="ai_usage_period_sep">—</span>
            <span>
              До
              {' '}
              {formatWhen(toLocal)}
            </span>
            <span className="ai_usage_period_note">конец не включается</span>
          </>
        )}
        {loading ? (
          <span className="ai_usage_period_loading">
            <MyLoading height={22} width={22} strokeWidth={4} visible />
          </span>
        ) : null}
      </div>

      {parseRun ? (
        <div className="ai_usage_chip">
          <span>
            Прогон
            {' '}
            {parseRun}
          </span>
          <button type="button" onClick={() => applyParseRun('')}>Сбросить</button>
        </div>
      ) : null}

      {error ? (
        <div className="ai_usage_error" role="alert">{error}</div>
      ) : null}

      {loading && !data && !error ? (
        <div className="ai_usage_loading">
          <MyLoading strokeWidth={4} width={40} height={40} visible />
        </div>
      ) : null}

      {totals ? (
        <div className="ai_usage_cards">
          {cards.map((card) => (
            <div key={card.key} className={`ai_usage_card${card.accent ? ' accent' : ''}`}>
              <div className="ai_usage_card_label">{card.label}</div>
              <div className="ai_usage_card_value">{card.value}</div>
            </div>
          ))}
        </div>
      ) : null}

      {totals && !reasonRows.length && !loading ? (
        <p className="ai_usage_empty">Нет запросов за выбранный период</p>
      ) : null}

      {reasonRows.length ? (
        <section className="ai_usage_section">
          <h2 className="ai_usage_section_title">По типам запросов</h2>
          <p className="ai_usage_hint">Нажмите на тип, чтобы оставить только эти запросы.</p>
          <div className="ai_usage_table_wrap">
            <table className="ai_usage_table">
              <thead>
                <tr>
                  <th>Тип</th>
                  <th className="num">Запросы</th>
                  <th className="num">Входные</th>
                  <th className="num">Выходные</th>
                  <th className="num">Всего токенов</th>
                  <th className="num">Веб-поиск</th>
                  <th className="num">Стоимость</th>
                </tr>
              </thead>
              <tbody>
                {reasonRows.map((row) => {
                  const share = sharePercent(row, totals);
                  const active = reason === row.reason;
                  return (
                    <tr key={row.reason} className={active ? 'active' : ''}>
                      <td>
                        <button
                          type="button"
                          className="ai_usage_reason_btn"
                          onClick={() => toggleReason(row.reason)}
                        >
                          {reasonLabel(row.reason)}
                        </button>
                        <div className="ai_usage_share" title={`${share.toFixed(1)}% от выбранного периода`}>
                          <span style={{ width: `${Math.min(100, Math.max(0, share))}%` }} />
                        </div>
                      </td>
                      <td className="num">{formatInt(row.requests)}</td>
                      <td className="num">{formatInt(row.prompt_tokens)}</td>
                      <td className="num">{formatInt(row.completion_tokens)}</td>
                      <td className="num">{formatInt(row.total_tokens)}</td>
                      <td className="num">{formatInt(row.web_search_calls)}</td>
                      <td className="num" title={row.cost_usd != null ? String(row.cost_usd) : undefined}>
                        {formatUsd(row.cost_usd)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {totals && (items.length || total > 0) ? (
        <section className="ai_usage_section">
          <h2 className="ai_usage_section_title">
            Журнал запросов
            <span>{formatInt(total)}</span>
          </h2>
          {items.length ? (
            <div className="ai_usage_table_wrap">
              <table className="ai_usage_table ai_usage_table_log">
                <thead>
                  <tr>
                    <th>Когда</th>
                    <th>Источник</th>
                    <th>Тип</th>
                    <th>Запрос</th>
                    <th>Модель</th>
                    <th className="num">Токены</th>
                    <th className="num">Поиск</th>
                    <th className="num">Стоимость</th>
                    <th>Прогон</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item._id}>
                      <td className="ai_usage_nowrap">{formatWhen(item.at)}</td>
                      <td>{sourceLabel(item.source)}</td>
                      <td>{reasonLabel(item.reason)}</td>
                      <td className="ai_usage_request_cell">
                        <div>{item.event_name || '—'}</div>
                        {item.reason_text ? (
                          <div className="ai_usage_muted" title={item.reason_text}>{item.reason_text}</div>
                        ) : null}
                      </td>
                      <td className="ai_usage_nowrap">{item.model || '—'}</td>
                      <td className="num">
                        <div>{formatInt(item.total_tokens)}</div>
                        <div className="ai_usage_muted">
                          {formatInt(item.prompt_tokens)}
                          {' '}
                          вход ·
                          {' '}
                          {formatInt(item.completion_tokens)}
                          {' '}
                          выход
                          {item.cached_tokens ? ` · кэш ${formatInt(item.cached_tokens)}` : ''}
                        </div>
                      </td>
                      <td className="num">{item.web_search_calls ? formatInt(item.web_search_calls) : '—'}</td>
                      <td className="num" title={item.cost_usd != null ? String(item.cost_usd) : undefined}>
                        {formatUsd(item.cost_usd)}
                      </td>
                      <td>
                        {item.parse_run ? (
                          <button
                            type="button"
                            className="ai_usage_link"
                            title="Показать запросы этого прогона"
                            onClick={() => applyParseRun(item.parse_run)}
                          >
                            {item.parse_run}
                          </button>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="ai_usage_empty">На этой странице нет записей</p>
          )}
          {pagesCount > 1 ? (
            <TablePagination page={page} setPage={setPage} pagesCount={pagesCount} />
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

export default OpenAiUsage;
